import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import {
  createSitemapUrlRequest,
  getParentSitemaps,
  getSitemapUrls,
  removeSitemapUrl,
  updateSitemapUrlRequest,
} from "../../../../services/api/landing/sitemapUrl/sitemapUrlApi";

const getLang = () =>
  localStorage.getItem("language") === "ar" ? "ar" : "en";

const toastMessages = {
  add: {
    en: "Sitemap URL created successfully!",
    ar: "تم إنشاء رابط الخريطة بنجاح!",
  },
  addError: {
    en: "Failed to create sitemap URL.",
    ar: "فشل في إنشاء رابط الخريطة.",
  },
  update: {
    en: "Sitemap URL updated successfully!",
    ar: "تم تحديث رابط الخريطة بنجاح!",
  },
  updateError: {
    en: "Failed to update sitemap URL.",
    ar: "فشل في تحديث رابط الخريطة.",
  },
  delete: {
    en: "Sitemap URL deleted successfully!",
    ar: "تم حذف رابط الخريطة بنجاح!",
  },
  deleteError: {
    en: "Failed to delete sitemap URL.",
    ar: "فشل في حذف رابط الخريطة.",
  },
};

export const fetchSitemapUrls = createAsyncThunk(
  "sitemapUrls/fetchSitemapUrls",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getSitemapUrls();
      return response.data.data;
    } catch (error) {
      return rejectWithValue("Failed to fetch sitemap URLs");
    }
  },
);

export const fetchParentSitemaps = createAsyncThunk(
  "sitemapUrls/fetchParentSitemaps",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getParentSitemaps();
      return response.data.data;
    } catch (error) {
      return rejectWithValue("Failed to fetch parent sitemaps");
    }
  },
);

export const createSitemapUrl = createAsyncThunk(
  "sitemapUrls/createSitemapUrl",
  async (sitemapData, { rejectWithValue }) => {
    try {
      const response = await createSitemapUrlRequest(sitemapData);
      return response.data.data;
    } catch (error) {
      return rejectWithValue("Failed to create sitemap URL");
    }
  },
);

export const updateSitemapUrl = createAsyncThunk(
  "sitemapUrls/updateSitemapUrl",
  async ({ id, sitemapData }, { rejectWithValue }) => {
    try {
      const response = await updateSitemapUrlRequest(id, sitemapData);
      return response.data.data;
    } catch (error) {
      return rejectWithValue("Failed to update sitemap URL");
    }
  },
);

export const deleteSitemapUrl = createAsyncThunk(
  "sitemapUrls/deleteSitemapUrl",
  async (sitemapId, { rejectWithValue }) => {
    try {
      await removeSitemapUrl(sitemapId);
      return sitemapId;
    } catch (error) {
      return rejectWithValue("Failed to delete sitemap URL");
    }
  },
);

const sitemapUrlSlice = createSlice({
  name: "sitemapUrls",
  initialState: {
    sitemapData: [],
    parentSitemaps: [],
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchSitemapUrls.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSitemapUrls.fulfilled, (state, action) => {
        state.loading = false;
        state.sitemapData = action.payload;
      })
      .addCase(fetchSitemapUrls.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchParentSitemaps.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchParentSitemaps.fulfilled, (state, action) => {
        state.loading = false;
        state.parentSitemaps = action.payload;
      })
      .addCase(fetchParentSitemaps.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createSitemapUrl.fulfilled, (state, action) => {
        state.sitemapData.push(action.payload);
        toast.success(toastMessages.add[getLang()]);
      })
      .addCase(createSitemapUrl.rejected, (state, action) => {
        state.error = action.payload;
        toast.error(toastMessages.addError[getLang()]);
      })
      .addCase(updateSitemapUrl.fulfilled, (state, action) => {
        const index = state.sitemapData.findIndex(
          (sitemap) => sitemap.id === action.payload.id,
        );
        if (index !== -1) {
          state.sitemapData[index] = action.payload;
        }
        toast.success(toastMessages.update[getLang()]);
      })
      .addCase(updateSitemapUrl.rejected, (state, action) => {
        state.error = action.payload;
        toast.error(toastMessages.updateError[getLang()]);
      })
      .addCase(deleteSitemapUrl.fulfilled, (state, action) => {
        state.sitemapData = state.sitemapData.filter(
          (sitemap) => sitemap.id !== action.payload,
        );
        toast.success(toastMessages.delete[getLang()]);
      })
      .addCase(deleteSitemapUrl.rejected, (state, action) => {
        state.error = action.payload;
        toast.error(toastMessages.deleteError[getLang()]);
      });
  },
});

export default sitemapUrlSlice.reducer;
